import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { CalendarClock, X } from 'lucide-react';
import { toast } from 'sonner';

// Reminder slots (local time, 24h). Each fires once per day per till and stays until dismissed.
const SLOTS = [12, 15];
const WINDOW_HOURS = 3;

const daysLeft = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
  return Math.ceil((d - new Date()) / 86400000);
};
const fmtDate = (iso) => iso ? new Date(iso + (iso.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' }) : '';

const pendingSlot = () => {
  const now = new Date();
  const day = now.toISOString().slice(0, 10);
  const h = now.getHours() + now.getMinutes() / 60;
  return SLOTS.find(s => h >= s && h < s + WINDOW_HOURS && !localStorage.getItem(`expiry-nudge-${day}-${s}`)) ?? null;
};

export const ExpiryNudgeBanner = ({ items, storeName, onRefresh, onViewAll }) => {
  const [slot, setSlot] = useState(null);

  useEffect(() => {
    const check = () => {
      const s = pendingSlot();
      if (s !== null && slot === null) {
        onRefresh?.();
        setSlot(s);
        toast(`${s === 12 ? 'Midday' : 'Afternoon'} reminder: short-dated stock to sell`, { description: 'Check the expiry banner at the top of the till.' });
      }
    };
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, [slot, onRefresh]);

  const expiring = (items || [])
    .filter(i => (i.quantity || 0) > 0)
    .map(i => ({ ...i, days: daysLeft(i.expiry_date) }))
    .sort((a, b) => (a.days ?? 9999) - (b.days ?? 9999));

  if (slot === null || expiring.length === 0) return null;

  const dismiss = () => {
    localStorage.setItem(`expiry-nudge-${new Date().toISOString().slice(0, 10)}-${slot}`, '1');
    setSlot(null);
  };

  return (
    <div className="rounded-md border-2 border-rose-300 bg-rose-50 px-3 py-2 flex items-start gap-3" data-testid="expiry-nudge-banner">
      <CalendarClock className="w-6 h-6 text-rose-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-rose-900" data-testid="expiry-nudge-title">
          {slot === 12 ? 'Midday' : '3pm'} reminder — {expiring.length} short-dated line{expiring.length === 1 ? '' : 's'} at {storeName}. Try to sell these first:
        </p>
        <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
          {expiring.slice(0, 6).map(i => (
            <li key={i.id} className="text-xs text-rose-900" data-testid={`expiry-nudge-item-${i.id}`}>
              <span className="font-semibold">{i.product_name}</span>
              {' · '}{i.quantity} left · {i.days !== null && i.days <= 0 ? <span className="font-bold text-red-700">EXPIRED</span> : `${i.days}d (${fmtDate(i.expiry_date)})`}
              {i.discount_percent ? ` · ${i.discount_percent}% off → $${(i.discounted_price || 0).toFixed(2)}` : ''}
            </li>
          ))}
          {expiring.length > 6 && <li className="text-xs text-rose-700 italic">+{expiring.length - 6} more…</li>}
        </ul>
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <Button size="sm" variant="outline" onClick={onViewAll} className="border-rose-400 text-rose-900 hover:bg-rose-100 h-7 text-xs" data-testid="expiry-nudge-view-all">View list</Button>
        <Button size="sm" variant="ghost" onClick={dismiss} className="text-rose-700 hover:bg-rose-100 h-7 text-xs" data-testid="expiry-nudge-dismiss"><X className="w-3.5 h-3.5 mr-1" /> Done</Button>
      </div>
    </div>
  );
};
